import { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { AdvisorsShell } from '@/modules/advisorsPage/components/AdvisorsShell/AdvisorsShell'
import { OnboardingRoutePathsEnum } from '@/modules/onboarding/routes/onboardingRouteConfig.types'

import { useUserStore } from '@/store/user.store'

import { NoAccounts } from '@/components/@Shared/NoAccounts/NoAccounts'
import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import { Disclaimer } from '@/components/Disclaimer/Disclaimer'
import { Loader } from '@/components/Loader/Loader'

import {
  useAdvisorServiceAdvisorControllerGetAllAdvisors,
  useAdvisorServiceAdvisorControllerGrantAccess,
  useAdvisorServiceAdvisorControllerRevokeAccess,
  useAppointmentsServiceAppointmentsControllerGetAppointments,
  useBankingServiceBankingControllerGetAccounts,
} from '@/__generated__/queries'
import type {
  AccountData,
  AdvisorData,
  AppointmentData,
} from '@/__generated__/requests'

export function Component() {
  const [searchedAdvisor, setSearchedAdvisor] = useState<string>('')
  const [selectedAdvisor, setSelectedAdvisor] = useState<
    AdvisorData | undefined
  >(undefined)
  const [isChatOpen, setIsChatOpen] = useState(false)

  const user = useUserStore.use.user()
  const setUser = useUserStore.use.setUser()

  const {
    data: accountsData,
    isFetching: isAccountsLoading,
    isLoading: isAccountsFetching,
  } = useBankingServiceBankingControllerGetAccounts()

  const [
    isGrantAccessModalOpened,
    { close: handleGrantAccessModalClose, open: handleGrantAccessModalOpen },
  ] = useDisclosure(false)

  const [
    isRevokeAccessModalOpened,
    { close: handleRevokeAccessModalClose, open: handleRevokeAccessModalOpen },
  ] = useDisclosure(false)

  const { data: appointmentsData } =
    useAppointmentsServiceAppointmentsControllerGetAppointments()

  const grantAccessMutation = useAdvisorServiceAdvisorControllerGrantAccess()
  const revokeAccessMutation = useAdvisorServiceAdvisorControllerRevokeAccess()
  const {
    data: advisorData,
    isFetching: isAdvisorsFetching,
    isLoading: isAdvisorsLoading,
  } = useAdvisorServiceAdvisorControllerGetAllAdvisors({
    query: searchedAdvisor,
  })

  const isMutating = useIsMutating({
    exact: true,
  })

  const userAccounts = useMemo<AccountData[]>(
    () => accountsData?.accounts ?? [],
    [accountsData],
  )

  const advisorAccounts = useMemo<AdvisorData[]>(
    () => advisorData ?? [],
    [advisorData],
  )

  const findAdvisor = useCallback(
    (id: number) => advisorAccounts.find(advisor => advisor.id === id),
    [advisorAccounts],
  )

  const handleGrantAccess = useCallback(() => {
    if (!selectedAdvisor) return

    grantAccessMutation.mutate(
      {
        requestBody: {
          advisor: selectedAdvisor.id,
        },
      },
      {
        onSuccess: data => {
          setUser(data)

          notifications.show({
            color: 'green',
            message: `Access granted for ${selectedAdvisor.firstName} ${selectedAdvisor.lastName}.`,
            title: 'Access granted',
          })

          handleGrantAccessModalClose()
        },
      },
    )
  }, [
    grantAccessMutation,
    setUser,
    selectedAdvisor,
    handleGrantAccessModalClose,
  ])

  const handleRevokeAccess = useCallback(() => {
    if (!selectedAdvisor) return

    revokeAccessMutation.mutate(
      {
        requestBody: {
          advisor: selectedAdvisor.id,
        },
      },
      {
        onSuccess: data => {
          setUser(data)

          notifications.show({
            color: 'orange',
            message: `Access revoked for ${selectedAdvisor.firstName} ${selectedAdvisor.lastName}.`,
            title: 'Access revoked',
          })

          handleRevokeAccessModalClose()
        },
      },
    )
  }, [
    revokeAccessMutation,
    setUser,
    selectedAdvisor,
    handleRevokeAccessModalClose,
  ])

  const handleChatOpen = () => {
    setIsChatOpen(true)
  }

  const handleChatClose = () => {
    setIsChatOpen(false)
  }

  const handleSelectAdvisor = (id: number) => {
    setSelectedAdvisor(findAdvisor(id))
    handleChatClose()
  }

  const handleSearchAdvisor = (value: string) => {
    setSearchedAdvisor(value)
  }

  useEffect(() => {
    if (!selectedAdvisor) return
    setSelectedAdvisor(findAdvisor(selectedAdvisor.id))
  }, [advisorAccounts, selectedAdvisor, findAdvisor])

  if (!user?.isFinancialDataProvided)
    return (
      <Flex align="center" direction="column">
        <Disclaimer
          description="
          To connect with a planner, please complete the onboarding form. Click the button below,
          and we'll guide you through the process, highlighting any required fields."
          link={{
            label: 'Complete Onboarding',
            state: { highlightFinancial: true },
            target: OnboardingRoutePathsEnum.ONBOARDING_EDIT,
          }}
          maxWidth={500}
          title="Onboarding"
        />
      </Flex>
    )

  if (isAccountsLoading || isAccountsFetching)
    return <Loader isAuthenticatedLayout />

  if (!userAccounts.length) return <NoAccounts />

  const hasFutureAppointment = (
    appointmentsData as AppointmentData[] | undefined
  )?.some(
    appointment =>
      appointment.date &&
      new Date(appointment.date) > new Date() &&
      appointment.status !== 'CANCELLED',
  )

  const showDataAccessNotice =
    !!hasFutureAppointment &&
    (!user?.advisorsAccess || user.advisorsAccess.length === 0)

  return (
    <>
      {showDataAccessNotice && (
        <Disclaimer
          description="To get the most out of your session, we recommend granting your CFP® access to your financial data so they can prepare in advance."
          title="Get more out of your session"
        />
      )}

      <AdvisorsShell
        advisors={advisorAccounts}
        allowedAdvisors={user?.advisorsAccess ?? []}
        isChatOpen={isChatOpen}
        isLoading={isAdvisorsLoading || isAdvisorsFetching}
        isMutating={!!isMutating}
        onChatClose={handleChatClose}
        onChatOpen={handleChatOpen}
        onGrantAccess={handleGrantAccessModalOpen}
        onRevokeAccess={handleRevokeAccessModalOpen}
        onSearchAdvisor={handleSearchAdvisor}
        onSelectAdvisor={handleSelectAdvisor}
        searchedAdvisor={searchedAdvisor}
        selectedAdvisor={selectedAdvisor}
      />

      <ConfirmationModal
        button={{ label: 'Grant Access', variant: 'primary' }}
        centered
        description={`Are you sure you want to grant access for ${selectedAdvisor?.firstName} ${selectedAdvisor?.lastName}?`}
        isLoading={!!isMutating}
        onClose={handleGrantAccessModalClose}
        onConfirm={handleGrantAccess}
        opened={isGrantAccessModalOpened}
        title="Grant access"
      />

      <ConfirmationModal
        button={{ label: 'Revoke Access', variant: 'error' }}
        centered
        description={`Are you sure you want to revoke access for ${selectedAdvisor?.firstName} ${selectedAdvisor?.lastName}?`}
        isLoading={!!isMutating}
        onClose={handleRevokeAccessModalClose}
        onConfirm={handleRevokeAccess}
        opened={isRevokeAccessModalOpened}
        title="Revoke access"
      />
    </>
  )
}

Component.displayName = 'Advisors'
